import React, { useMemo, useRef, useEffect } from 'react';
import Modal from 'react-modal';
import { useQuery } from '@tanstack/react-query';
import styles from './AchievementsModal.module.scss';
import { Icon, IconName } from '../../../assets/icons/Icon';
import { useApiClient } from '../../../api/ApiClientContext';
import { BadgeResponse, BadgesResponse, MilestonesResponse } from '../../../api/Api';
import { RingProgress } from '@mantine/core';

interface AchievementsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BADGE_ICON_MAP: Record<number, IconName> = {
  1: 'HabitDev2',
  2: 'HabitDev5',
  3: 'HabitDev10',
  4: 'HabitDev15',
  5: 'HabitDev30',
  6: 'Streak3',
  7: 'Streak5',
  8: 'Streak7',
  9: 'Streak10',
  10: 'Streak15',
  11: 'EvaluateHabit1',
  12: 'EvaluateHabit2',
  13: 'EvaluateHabit3',
  14: 'EvaluateHabit4',
  15: 'EvaluateHabit5',
  16: 'PeersEndorsement1',
  17: 'PeersEndorsement2',
  18: 'PeersEndorsement3',
  19: 'PeersEndorsement4',
  20: 'PeersEndorsement5',
};

const CATEGORY_META: Record<string, { title: string; lineColor: string }> = {
  'Habit Development': { title: 'HABIT DEVELOPMENT', lineColor: '#CFBFFF' },
  'Streak': { title: 'STREAK', lineColor: '#BAC8FF' },
  'Habit Evaluation': { title: 'HABIT EVALUATION', lineColor: '#96F2D7' },
  'Team Work': { title: 'TEAM WORK', lineColor: '#FFD8A8' },
};

const CATEGORY_ORDER = ['Habit Development', 'Streak', 'Habit Evaluation', 'Team Work'];

const getMilestoneRingCol = (level: number) => {
  switch (level) {
    case 1: return 'hsla(228, 89%, 63%, 1)';
    case 2: return 'hsla(187, 80%, 42%, 1)';
    case 3: return '#7950F2';
    case 4: return '#FD7E14';
    default: return '#4C6EF5';
  }
};

const getMilestoneIconBg = (level: number) => {
  switch (level) {
    case 1: return '#EDF2FF';
    case 2: return '#E3FAFC';
    case 3: return '#F3F0FF';
    case 4: return '#FFF4E6';
    default: return '#EDF2FF';
  }
};

const BadgeCard: React.FC<{ badge: BadgeResponse }> = ({ badge }) => {
  const iconName = BADGE_ICON_MAP[badge.id];
  const isLocked = badge.status === 'locked';
  const isAchieved = badge.status === 'achieved';
  const progress = badge.progress as { current: number; target: number } | null;

  let progressPct = 0;
  if (isAchieved) {
    progressPct = 100;
  } else if (progress) {
    progressPct = Math.min((progress.current / progress.target) * 100, 100);
  }

  return (
    <div className={styles.badge}>
      <div className={styles.badgeIconWrap}>
        {isLocked ? (
          <Icon iconName="BadgeLocked" width="5.5rem" height="5.5rem" />
        ) : iconName ? (
          <Icon iconName={iconName} width="5.5rem" height="5.5rem" />
        ) : null}
        {isAchieved && badge.counter >= 1 && (
          <span className={styles.badgeCounter}>
            {badge.counter > 99 ? '99+' : badge.counter}
          </span>
        )}
      </div>
      <span className={styles.badgeLabel}>{badge.name}</span>
      <div className={styles.progressBar}>
        <div className={styles.progressTrack}>
          <div
            className={styles.progressFill}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export const AchievementsModal: React.FC<AchievementsModalProps> = ({ isOpen, onClose }) => {
  const apiClient = useApiClient();
  const prevBadgesRef = useRef<BadgeResponse[] | null>(null);

  const { data: badgesData } = useQuery<BadgesResponse>({
    queryKey: ['badges'],
    queryFn: async () => {
      const res = await apiClient.api.getBadgesApiV1AchievementsBadgesGet();
      return res?.data;
    },
    enabled: isOpen,
  });

  const { data: milestonesData } = useQuery<MilestonesResponse>({
    queryKey: ['insightsMilestones'],
    queryFn: async () => {
      const res = await apiClient.api.getMilestonesApiV1InsightsMilestonesGet();
      return res?.data;
    },
    enabled: isOpen,
  });

  const newlyEarned = useMemo(() => {
    if (!badgesData?.badges || !prevBadgesRef.current) return [];
    const prev = prevBadgesRef.current;
    return badgesData.badges.filter((b) => {
      const old = prev.find((p) => p.id === b.id);
      return b.status === 'achieved' && old && old.status !== 'achieved';
    });
  }, [badgesData]);

  useEffect(() => {
    if (badgesData?.badges) {
      prevBadgesRef.current = badgesData.badges;
    }
  }, [badgesData]);

  const grouped = useMemo(() => {
    if (!badgesData?.badges) return {};
    const map: Record<string, BadgeResponse[]> = {};
    badgesData.badges.forEach((b) => {
      if (!map[b.category]) map[b.category] = [];
      map[b.category].push(b);
    });
    return map;
  }, [badgesData]);

  const milestoneHabits = milestonesData?.habits ?? [];
  const levelNames = ['Explorer', 'Practitioner', 'Expert', 'Role model'];
  const milestoneIconNames = ['MilestonesLevel1', 'MilestonesLevel2', 'MilestonesLevel3', 'MilestonesLevel4'] as const;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Achievements"
      ariaHideApp={false}
      className={styles.modal}
      overlayClassName={styles.overlay}
    >
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLogo}>
            <Icon iconName="Logo" width="8rem" height="1.88rem" />
          </div>
          <h1 className={styles.title}>Achievements</h1>
          <button className={styles.closeButton} onClick={onClose}>
            <Icon iconName="CrossIcon" width="1.5rem" height="1.5rem" />
          </button>
        </div>

        {/* Main Content */}
        <div className={styles.content}>
          <div className={styles.contentInner}>
            {/* Sidebar - Up Next */}
            <div className={styles.sidebar}>
              <nav className={styles.breadcrumb}>
                <button className={styles.breadcrumbLink} onClick={onClose}>Home</button>
                <span className={styles.breadcrumbSep}>/</span>
                <span className={styles.breadcrumbCurrent}>Achievements</span>
              </nav>
              <div className={styles.sidebarCard}>
                <h3 className={styles.sidebarTitle}>Up next</h3>
                <div className={styles.upNextList}>
                  {badgesData?.up_next?.length ? (
                    badgesData.up_next.map((item, i) => {
                      const matchedBadge = badgesData.badges.find((b) => b.name === item.name);
                      const badgeIcon = matchedBadge ? BADGE_ICON_MAP[matchedBadge.id] : null;

                      return (
                        <div key={i} className={styles.upNextItem}>
                          <div className={styles.upNextIcon}>
                            {badgeIcon && (
                              <Icon iconName={badgeIcon} width="2.5rem" height="2.5rem" />
                            )}
                          </div>
                          <div className={styles.upNextInfo}>
                            <span className={styles.upNextTitle}>{item.name}</span>
                            <span className={styles.upNextDescription}>{item.description}</span>
                          </div>
                        </div>
                      );
                    })
                  ) : badgesData && badgesData.collected_count === badgesData.total_count ? (
                    <div className={styles.upNextItem}>
                      <span className={styles.upNextTitle}>All badges collected!</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Main Area */}
            <div className={styles.mainArea}>
              {/* Milestones Section */}
              <section className={styles.milestonesSection}>
                <h2 className={styles.sectionTitle}>Milestones</h2>
                {milestoneHabits.map((habit) => {
                  return (
                    <div key={habit.user_habit_id} className={styles.milestoneHabit}>
                      <div className={styles.milestoneHabitHeader}>
                        <Icon iconName="OneIcon" width="1.5rem" height="1.5rem" />
                        <span className={styles.milestoneHabitName}>{habit.habit_name}</span>
                      </div>
                      <div className={styles.milestonesTrack}>
                        {[1, 2, 3, 4].map((level, idx) => {
                          const isReached = level <= habit.current_level;
                          const isCurrent = level === habit.current_level;
                          const iconBg = isReached ? getMilestoneIconBg(level) : '#F1F3F5';
                          const ringCol = isReached ? getMilestoneRingCol(level) : '#DEE2E6';
                          let ringValue = 0;
                          if (level < habit.current_level) {
                            ringValue = 100;
                          } else if (level === habit.current_level && habit.next_level) {
                            const reqs = habit.next_level.requirements;
                            const avg = reqs.reduce((sum, r) => sum + Math.min(r.current / r.target, 1), 0) / reqs.length;
                            ringValue = Math.round(avg * 100);
                          }

                          const hintForGap = isCurrent && habit.next_level
                            ? (() => {
                                const logsReq = habit.next_level.requirements.find((r) => r.type === 'logs');
                                if (logsReq && !logsReq.met) {
                                  const remaining = logsReq.target - logsReq.current;
                                  return `Track habit ${remaining} more time${remaining !== 1 ? 's' : ''}`;
                                }
                                const piReq = habit.next_level.requirements.find((r) => r.type === 'positive_impact');
                                if (piReq && !piReq.met) return 'Log a positive impact';
                                const endReq = habit.next_level.requirements.find((r) => r.type === 'endorsement_given');
                                if (endReq && !endReq.met) return 'Endorse a teammate';
                                return null;
                              })()
                            : null;

                          return (
                            <React.Fragment key={level}>
                              <div className={styles.milestone}>
                                <RingProgress
                                  size={48}
                                  thickness={3}
                                  roundCaps
                                  sections={[{ value: ringValue, color: ringCol }]}
                                  label={
                                    <div style={{
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      width: '100%', height: '100%',
                                    }}>
                                      <div style={{
                                        width: '2.2rem', height: '2.2rem', borderRadius: '50%',
                                        backgroundColor: iconBg,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      }}>
                                        <Icon iconName={milestoneIconNames[idx]} width="1.4rem" height="1.4rem" />
                                      </div>
                                    </div>
                                  }
                                />
                                <span className={styles.milestoneLabel}>Level {level}</span>
                                <span className={styles.milestoneDescription}>{levelNames[idx]}</span>
                              </div>
                              {idx < 3 && (
                                <div className={styles.milestoneConnector}>
                                  <div className={styles.milestoneLine} />
                                  {hintForGap && (
                                    <span className={styles.milestoneHint}>{hintForGap}</span>
                                  )}
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </section>

              {/* Badges Section */}
              <section className={styles.badgesSection}>
                <div className={styles.badgesHeader}>
                  <h2 className={styles.sectionTitle}>Badges</h2>
                  {badgesData && (
                    <span className={styles.badgesCount}>
                      {badgesData.collected_count}/{badgesData.total_count} collected
                    </span>
                  )}
                </div>

                {CATEGORY_ORDER.map((cat) => {
                  const meta = CATEGORY_META[cat];
                  const badges = grouped[cat];
                  if (!badges?.length) return null;

                  return (
                    <div key={cat} className={styles.category}>
                      <h3 className={styles.categoryTitle}>{meta.title}</h3>
                      <div className={styles.badgeGrid}>
                        {badges.map((badge) => (
                          <BadgeCard key={badge.id} badge={badge} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </section>

              {/* Congrats for newly earned */}
              {newlyEarned.length > 0 && (
                <div className={styles.congratsBanner}>
                  <h3>{newlyEarned.length === 1
                    ? newlyEarned[0].copy?.congrats_title
                    : `You earned ${newlyEarned.length} new badges!`
                  }</h3>
                  <p>{newlyEarned.length === 1
                    ? newlyEarned[0].copy?.congrats_body
                    : newlyEarned.map((b) => b.name).join(', ')
                  }</p>
                </div>
              )}

              {/* Footer */}
              <div className={styles.footer}>
                <button className={styles.backButton} onClick={onClose}>
                  Back to home
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AchievementsModal;
